import React, { useEffect, useState, useCallback, FormEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'ui/shared/containers';
import { DeviceId } from 'shared/models';
import { useWebSocket } from 'hooks/useWebSocket/useWebSocket';

require('./EditFileDialog.css');

export type Props = {
    visible: boolean;
    deviceId: DeviceId;
    filename: string | null;
    readOnly: boolean;
    onClose(): void;
};

const noop = () => {};

// Wait for up to 15 seconds for the file to be loaded from the remote device
const TIMEOUT = 15000;

export const EditFileDialog = ({ visible, deviceId, filename, readOnly, onClose }: Props) => {
    const { t } = useTranslation();
    const websocketClient = useWebSocket();

    const loadingTimerRef = useRef<NodeJS.Timer>();

    const [ originalContent, setOriginalContent ] = useState('');
    const [ content, setContent ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState<string>();

    const handleContentChange = useCallback((e: FormEvent<HTMLTextAreaElement>) => {
        const { value } = e.currentTarget;
        setContent(value);
    }, [ setContent ]);

    useEffect(() => {
        if (visible && deviceId && filename && !loading && !originalContent && !errorMessage) {
            setLoading(true);
            loadingTimerRef.current = setTimeout(() => {
                setLoading(false);
                setErrorMessage(t('ERROR.TIMEOUT'));
            }, TIMEOUT);
            websocketClient.ftpRead(deviceId, filename);

            return () => {
                clearTimeout(loadingTimerRef.current!);
            };
        }
    }, [ visible, deviceId, filename, loading, originalContent, errorMessage, websocketClient, setLoading, loadingTimerRef, t ]);

    const handleMessage = useCallback((msg: any) => {
        if (msg.scope === 'FTP') {
            switch (msg.type) {
                case 'FILE':
                    if (msg.deviceId === deviceId && msg.filename === filename) {
                        const content = atob(msg.content);
                        setOriginalContent(content);
                        setContent(content);
                        setLoading(false);
                        clearTimeout(loadingTimerRef.current!);
                    }
                    break;
            }
        }
    }, [ deviceId, filename, setOriginalContent, setContent, setLoading, loadingTimerRef ]);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        websocketClient.ftpWrite(deviceId, filename!, content);
        setOriginalContent(content);
        e.preventDefault();
        return false;
    }, [ websocketClient, deviceId, filename, content, setOriginalContent ]);

    useEffect(() => {
        setLoading(false);
        setContent('');
        setOriginalContent('');
        setErrorMessage(undefined);
    }, [ deviceId, filename, setLoading, setContent, setOriginalContent, setErrorMessage ]);

    useEffect(() => {
        websocketClient.on('message', handleMessage);
        return () => {
            websocketClient.off('message', handleMessage);
        };
    }, [ websocketClient, handleMessage ]);

    return (
        <Dialog
            id="edit-file-dialog"
            visible={ visible }
        >
            <form
                onSubmit={ handleSubmit }
            >
                <textarea
                    value={ loading ? t('LOADING') as string : errorMessage || content }
                    onChange={ loading || errorMessage ? noop : handleContentChange }
                    readOnly={ loading || readOnly || !!errorMessage }
                />
                <div>
                    { !readOnly &&
                        <button
                            id="edit-file-dialog-save"
                            className="ui primary red button"
                            type="submit"
                            disabled={ content === originalContent || !!errorMessage || !websocketClient.connected }
                        >
                            { t('COMMAND.SAVE') }
                        </button>
                    }
                    <button
                        id="edit-file-dialog-cancel"
                        className="ui grey button"
                        type="button"
                        onClick={ onClose }
                    >
                        { t(content === originalContent ? 'COMMAND.CLOSE' : 'COMMAND.CANCEL') }
                    </button>
                </div>
            </form>
        </Dialog>
    );
};
